import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as _ from 'underscore';

import { appStore } from '../../../app/AppStore';
import { CommonStyles } from '../CommonStyles';
import { IReactTranslateService } from '../IReactTranslateService';
import { SorterActions } from '../SorterActions';
import { DeprecatedReactMenu } from './Menu';
import { Select } from './Select';

/**
 * A drop down menu that has separate sections for the sort key and available directions.
 *
 * TODO: Keyboard navigation
 *       Auto size Select to match width of largest panel item
 */
// TODO: Remove this when the old ProjectGrid / WorkbookGrid/ DatasourceGrid code is deleted
export class SortTool extends React.PureComponent<SortTool.Props, SortTool.State> {

  public static displayName = 'SortTool';
  public static element = React.createFactory(SortTool);

  private unsubscribe: () => void;
  private boundUpdateOrderFromStore = this.updateOrderFromStore.bind(this);
  private boundSelectSortDirection = this.selectSortDirection.bind(this);
  private boundSelectSortOption = this.selectSortOption.bind(this);
  private selectButtonWidth: number;

  // refs
  private menu: DeprecatedReactMenu;
  private selectButton: Select.Button;

  public componentWillMount() {
    this.updateOrderFromStore();
    this.unsubscribe = appStore.subscribe(this.boundUpdateOrderFromStore);
  }

  public componentDidMount() {
    this.selectButtonWidth = $(ReactDOM.findDOMNode(this.selectButton)).width();
  }

  public componentWillUnmount() {
    this.unsubscribe();
  }

  public render() {
    return DeprecatedReactMenu.element({
      style: {
        display: 'inline-block',
        verticalAlign: 'top',
        height: CommonStyles.Sizes.RowHeight,
        lineHeight: CommonStyles.Sizes.RowHeight
      },
      ref: (menu: DeprecatedReactMenu) => this.menu = menu,
      tbTestId: this.props.tbTestId || 'sort-tool',
      menuButtonFactory: (() => {
        return Select.Button.element(
          {
            ref: (selectButton: Select.Button) => this.selectButton = selectButton,
            style: this.props.style,
          }, this.getSelectButtonText());
      }) as React.Factory<DeprecatedReactMenu.ButtonProps>,
      menuPanelStyle: {
        position: 'relative',
        top: -1
      },
      menuPanelFactory: () => {

        const sortOptionsPanel = Select.ListPanel.element({
          items: _.map(this.props.sortOptions, (sortOption: SortTool.SortOption) => {
            return {
              id: sortOption.field,
              text: sortOption.locFn()
            };
          }),
          onItemSelected: this.boundSelectSortOption,
          selectedItemId: this.state.orderField
        });

        let directionOptionsPanel: React.ReactElement<any>;
        const selectedSortOption = _.findWhere(this.props.sortOptions, { field: this.state.orderField });
        const directionOptions = selectedSortOption && selectedSortOption.directionOptions;

        if (directionOptions) {
          directionOptionsPanel = React.createElement('div', {},
            this.horizontalDivider(),
            Select.ListPanel.element({
              items: [
                {
                  id: 'asc',
                  text: directionOptions.asc.locFn()
                },
                {
                  id: 'desc',
                  text: directionOptions.desc.locFn()
                }
              ],
              onItemSelected: this.boundSelectSortDirection,
              selectedItemId: this.state.orderDirection
            })
          );
        }
        return React.createElement('div', {
          style: {
            borderWidth: CommonStyles.Sizes.TextBoxBorderSize,
            borderColor: CommonStyles.Colors.BorderColor,
            borderStyle: 'solid',
            backgroundColor: CommonStyles.Colors.White,
            padding: CommonStyles.Sizes.SmallSpace,
            boxShadow: CommonStyles.Shadows.MenuShadow,

            // Make the width of the panel match the width of the select button.
            width: this.selectButtonWidth
          },
        }, sortOptionsPanel, directionOptionsPanel);
      }
    });
  }

  private getSelectButtonText() {

    const selectedSortOption = _.findWhere(this.props.sortOptions, { field: this.state.orderField });
    if (!selectedSortOption) {
      return '';
    }

    const orderDirection = this.state.orderDirection;

    if (selectedSortOption.directionOptions) {
      // If there are multiple direction options, use the loc key for the correct one.
      const directionOptions = selectedSortOption.directionOptions;
      return directionOptions[orderDirection].selectButtonLocFn();
    } else if (orderDirection === selectedSortOption.direction) {
      // otherwise, use the loc key for the option itself, but only if the direction matches.
      return selectedSortOption.locFn();
    } else {
      return '';
    }
  }

  private horizontalDivider() {
    return React.createElement('div', {
      style: {
        borderTopWidth: CommonStyles.Sizes.TextBoxBorderSize,
        borderTopColor: CommonStyles.Colors.BorderColor,
        borderTopStyle: 'solid',
        margin: CommonStyles.Sizes.SmallSpace
      }
    });
  }

  private updateOrderFromStore() {
    const order = appStore.getState().order;
    const newOrderState: SortTool.State = { orderField: null, orderDirection: null };

    if (order && order.length) {
      newOrderState.orderField = order[0].field;
      newOrderState.orderDirection = order[0].ascending ? 'asc' : 'desc';
    }

    this.setState(newOrderState);
  }

  private selectSortOption(field: string) {
    this.menu.closeMenu();

    const sortOption = _.findWhere(this.props.sortOptions, { field: field });
    const direction = sortOption.direction;

    // do nothing if the same field and direction are selected
    if (field === this.state.orderField
      && direction === this.state.orderDirection) {
      return;
    }

    this.setOrder(field, direction);
  }

  private selectSortDirection(direction: string) {
    this.menu.closeMenu();

    // do nothing if the same order is selected
    if (direction === this.state.orderDirection) {
      return;
    }

    this.setOrder(this.state.orderField, direction);
  }

  private setOrder(field: string, direction: string) {
    this.props.sorterActions.setOrder([{ field: field, ascending: direction === 'asc' }]);
  }

}

export module SortTool {
  export interface Props extends React.Props<SortTool> {
    translate: IReactTranslateService;
    sorterActions: SorterActions;
    sortOptions: SortOption[];
    style?: React.CSSProperties;
    tbTestId?: string;
  }

  export interface State {
    orderField: string;
    orderDirection: string;
  }

  /**
   * SortOption
   *
   * @property field             The order field that will be used to order the results by.
   * @property locKey            The locKey for the sort option. eg. "Name".
   * @property direction         The default direction that is used when this option is selected.
   * @property directionOptions  An optional DirectionOptions object to use when a field can be sorted ascending or descending.
   */
  export interface SortOption {
    field: string;
    locFn: () => string; // A `Messages` function;
    direction: string;
    directionOptions?: DirectionOptions;
  }

  export interface DirectionOptions extends _.Dictionary<DirectionOption> {
    asc: DirectionOption;
    desc: DirectionOption;
  }

  /**
   * DirectionOption
   *
   * @property locKey              The locKey for the direction option. eg. "Ascending".
   * @property selectButtonLocKey  The locKey for the text that is shown in the header when this field/direction combo is selected.
   *                               eg. "Name (A-Z)"
   */
  export interface DirectionOption {
    locFn: () => string; // A `Messages` function;
    selectButtonLocFn: () => string; // A `Messages` function;
  }

}

